import { router } from "expo-router";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Pressable, View } from "react-native";
import { Text } from "./ui/BrandText";
import { Alert } from "../lib/productAlert";
import { getChatErrorMessage } from "../lib/chat";
import { startIdentityDm } from "../lib/directMessaging";
import {
  blockIdentity,
  getIdentityBlockStatus,
  reportIdentityMessaging,
  unblockIdentity,
} from "../lib/identityMessagingSafety";
import { useActiveAccount } from "../hooks/useActiveAccount";
import { border, surface, text as uiText } from "../theme/tokens";
import type { PublicIdentitySnapshot } from "../types/identity";


export function ProfileActions({ identity, children, onReport, onToggleBlock, blocked = false }: { identity: PublicIdentitySnapshot; children?: React.ReactNode; onReport?: () => void; onToggleBlock?: () => void; blocked?: boolean }) {
  const { activeAccountId, accounts } = useActiveAccount();
  const [contacting, setContacting] = useState(false);
  const [safetyBusy, setSafetyBusy] = useState(false);
  const [identityBlocked, setIdentityBlocked] = useState(false);
  const displayName = identity.displayName.trim() || identity.handle.trim() || "Utilisateur";
  const isControlledIdentity = useMemo(
    () => accounts.some((account) => account.accountId === identity.identityId),
    [accounts, identity.identityId]
  );
  const canContact = !!activeAccountId && !isControlledIdentity && !identityBlocked && !blocked;
  const showInstitutionSafety = identity.accountType === "institution"
    && !!activeAccountId
    && !isControlledIdentity;

  useEffect(() => {
    if (!showInstitutionSafety) {
      setIdentityBlocked(false);
      return;
    }
    let active = true;
    void getIdentityBlockStatus(identity.identityId)
      .then((blocked) => { if (active) setIdentityBlocked(blocked); })
      .catch(() => {});
    return () => { active = false; };
  }, [identity.identityId, showInstitutionSafety]);

  const contact = useCallback(async () => {
    if (!canContact || !activeAccountId || contacting) return;
    try {
      setContacting(true);
      const result = await startIdentityDm(activeAccountId, identity.identityId);
      router.push({ pathname: "/(tabs)/messages/direct/[id]", params: { id: result.conversation.id } });
    } catch (error) {
      Alert.alert("Discussion indisponible", getChatErrorMessage(error, "Impossible d'ouvrir cette discussion."));
    } finally {
      setContacting(false);
    }
  }, [activeAccountId, canContact, contacting, identity.identityId]);

  const toggleInstitutionBlock = useCallback(async () => {
    if (!showInstitutionSafety || safetyBusy) return;
    try {
      setSafetyBusy(true);
      if (identityBlocked) {
        await unblockIdentity(identity.identityId);
        setIdentityBlocked(false);
        Alert.alert("École débloquée", "Cette identité peut de nouveau te contacter.");
      } else {
        await blockIdentity(identity.identityId);
        setIdentityBlocked(true);
        Alert.alert("École bloquée", "Cette identité ne peut plus démarrer ni poursuivre une discussion avec toi.");
      }
    } catch (error) {
      Alert.alert("Action impossible", getChatErrorMessage(error, "Impossible de mettre à jour ce blocage."));
    } finally {
      setSafetyBusy(false);
    }
  }, [identity.identityId, identityBlocked, safetyBusy, showInstitutionSafety]);

  const reportInstitution = useCallback(async () => {
    if (!showInstitutionSafety || !activeAccountId || safetyBusy) return;
    try {
      setSafetyBusy(true);
      await reportIdentityMessaging({
        reporterIdentityId: activeAccountId,
        targetIdentityId: identity.identityId,
        reason: "other",
        details: "Signalement depuis le profil public institutionnel",
      });
      Alert.alert("Signalement envoyé", "Merci, cette identité sera examinée par la modération.");
    } catch (error) {
      Alert.alert("Signalement impossible", getChatErrorMessage(error, "Impossible d'envoyer ce signalement."));
    } finally {
      setSafetyBusy(false);
    }
  }, [activeAccountId, identity.identityId, safetyBusy, showInstitutionSafety]);

  if (isControlledIdentity || !activeAccountId) return null;
  return (
    <View style={{ flexDirection: "row", flexWrap: "wrap", alignItems: "center", gap: 8, marginTop: 12 }}>
      {children}
      {canContact ? <Pressable onPress={() => void contact()} disabled={contacting} accessibilityRole="button" accessibilityLabel={`Contacter ${displayName}`} style={{ minHeight: 40, justifyContent: "center", paddingHorizontal: 12, borderRadius: 12, borderWidth: 1, borderColor: border.brand, backgroundColor: surface.selected, opacity: contacting ? 0.6 : 1 }}><Text style={{ color: uiText.primary, fontWeight: "800" }}>{contacting ? "Ouverture…" : "Contacter"}</Text></Pressable> : null}
      {showInstitutionSafety || onReport || onToggleBlock ? <Pressable accessibilityRole="button" accessibilityLabel="Actions du profil" disabled={safetyBusy} onPress={() => Alert.alert("Profil", undefined, [
        { text: "Signaler", onPress: onReport ?? (() => void reportInstitution()) },
        { text: (showInstitutionSafety ? identityBlocked : blocked) ? "Débloquer" : "Bloquer", style: "destructive", onPress: onToggleBlock ?? (() => void toggleInstitutionBlock()) },
        { text: "Annuler", style: "cancel" },
      ])} style={{ minWidth: 44, minHeight: 40, alignItems: "center", justifyContent: "center", borderRadius: 12, borderWidth: 1, borderColor: border.subtle, backgroundColor: surface.primary }}><Text style={{ color: uiText.primary, fontSize: 22 }}>…</Text></Pressable> : null}
    </View>
  );
}
